/**
 * Deep-link bootstrap for the storefront Mini App.
 *
 * The bot opens the Mini App in two shapes the hash router cannot see on its own:
 *
 *  - a ``web_app`` button URL with a real *path*, e.g. ``https://<host>/orders/12``
 *    (everything after the host is in ``location.pathname``, not the hash);
 *  - a ``t.me/<bot>/<app>?startapp=order_12`` deep link, where Telegram exposes the
 *    payload via ``start_param`` (``getStartParam`` → ``order_12``).
 *
 * Telegram also appends its own params to the hash (``#/orders/12&tgWebAppData=…``).
 *
 * {@link applyDeepLink} resolves all of this once, before the router reads the hash, by
 * rewriting ``location`` to a clean hash route via ``history.replaceState`` so the
 * existing hash router (and {@link readPathFromHash}) just works. It must run exactly
 * once at bootstrap (before React renders).
 */

/** Path prefixes we recognise as in-app routes when they arrive as a real path. */
const KNOWN_PATH_PREFIXES: readonly string[] = [
  "/orders",
  "/shop",
  "/referrals",
  "/earn",
  "/rpg",
  "/product",
  "/support",
];

/**
 * Strip Telegram's own params appended to a hash route.
 *
 * Telegram may turn ``#/orders/12`` into ``#/orders/12&tgWebAppData=…`` (or with
 * ``?``/``tgWebApp…``). We keep only the leading ``/path`` part — everything from the
 * first ``&``, ``?`` or ``tgWebApp`` marker onwards is dropped.
 */
export function stripTelegramHashParams(route: string): string {
  let result = route;
  for (const sep of ["&", "?"]) {
    const idx = result.indexOf(sep);
    if (idx !== -1) {
      result = result.slice(0, idx);
    }
  }
  const tg = result.indexOf("tgWebApp");
  if (tg !== -1) {
    result = result.slice(0, tg);
  }
  return result;
}

/** ``true`` when a path looks like one of the app's routes. */
export function isKnownAppPath(pathname: string): boolean {
  return KNOWN_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Map a ``start_param`` payload to a hash route, or ``null`` when it is not a routing
 * payload (e.g. ``ref_<id>`` referral payloads are handled elsewhere and left alone).
 */
export function startParamToRoute(startParam: string | null): string | null {
  if (startParam === null) {
    return null;
  }
  const orderMatch = /^order[_-](\d+)$/.exec(startParam);
  if (orderMatch && orderMatch[1] !== undefined) {
    return `/orders/${orderMatch[1]}`;
  }
  return null;
}

export interface DeepLinkEnv {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
  readonly startParam: string | null;
}

/**
 * Resolve the bootstrap location into a clean hash route, or ``null`` when no rewrite is
 * needed. Pure (no side effects) so it can be unit-tested.
 *
 * Priority: an explicit hash route (after stripping Telegram params) wins; then a real
 * path that looks like a route; then a ``start_param`` order deep-link.
 */
export function resolveDeepLink(env: DeepLinkEnv): string | null {
  const rawHash = env.hash.replace(/^#/, "");
  if (rawHash.startsWith("/")) {
    const cleaned = stripTelegramHashParams(rawHash);
    // Only rewrite if Telegram actually polluted the hash; otherwise leave it.
    return cleaned !== rawHash && cleaned.startsWith("/") ? cleaned : null;
  }

  if (env.pathname !== "/" && isKnownAppPath(env.pathname)) {
    return `${env.pathname}${env.search}`;
  }

  return startParamToRoute(env.startParam);
}

/**
 * Apply deep-link resolution to the live ``window`` once at bootstrap.
 *
 * Rewrites ``location`` to a clean ``/#<route>`` via ``history.replaceState`` so the hash
 * router reads the intended route. No-op outside a browser or when nothing matches.
 */
export function applyDeepLink(getStartParam: () => string | null): void {
  if (typeof window === "undefined") {
    return;
  }
  let startParam: string | null = null;
  try {
    startParam = getStartParam();
  } catch {
    startParam = null;
  }

  const route = resolveDeepLink({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    startParam,
  });

  if (route === null) {
    return;
  }

  // Always land on the app origin "/" with the route carried in the hash, so a real
  // path like /orders/12 (which the static server may 404) becomes /#/orders/12.
  window.history.replaceState(null, "", `/#${route}`);
}
